import React, { useState, useEffect } from 'react';
import { extractTailwindClasses } from '../../../utils/visualEditor/elementParser';

/**
 * Spacing Editor
 * Edit margins and padding for each side
 */
export default function SpacingEditor({ element, onUpdate }) {
  const [margin, setMargin] = useState({ top: 0, right: 0, bottom: 0, left: 0 });
  const [padding, setPadding] = useState({ top: 0, right: 0, bottom: 0, left: 0 });
  const [activeTab, setActiveTab] = useState('margin'); // 'margin' or 'padding'

  useEffect(() => {
    if (element) {
      const twClasses = extractTailwindClasses(element.element);
      
      // Parse margin classes
      const marginValues = parseSpacingClasses(twClasses.spacing.margin);
      setMargin(marginValues);
      
      // Parse padding classes
      const paddingValues = parseSpacingClasses(twClasses.spacing.padding);
      setPadding(paddingValues);
    }
  }, [element]);

  const handleMarginChange = (side, value) => {
    const newMargin = { ...margin, [side]: value };
    setMargin(newMargin);
    updateSpacing('margin', side, value);
  };

  const handlePaddingChange = (side, value) => {
    const newPadding = { ...padding, [side]: value };
    setPadding(newPadding);
    updateSpacing('padding', side, value);
  };

  const updateSpacing = (type, side, value) => {
    if (!element) return;
    
    // Get style path from element or infer it
    const stylePath = element.element?.getAttribute('data-style-path') || 
                     inferStylePath(element.dataPath);
    
    if (!stylePath) {
      console.warn('No style path found for spacing update');
      return;
    }
    
    // Map side to property name
    const propertyMap = {
      margin: {
        top: 'marginTop',
        bottom: 'marginBottom',
        left: 'marginLeft',
        right: 'marginRight'
      },
      padding: {
        top: 'paddingTop',
        bottom: 'paddingBottom',
        left: 'paddingLeft',
        right: 'paddingRight'
      }
    };
    
    const property = propertyMap[type][side];
    
    // Map side to Tailwind abbreviation
    const sideMap = { top: 't', right: 'r', bottom: 'b', left: 'l' };
    const prefix = type === 'margin' ? 'm' : 'p';
    const className = `${prefix}${sideMap[side]}-${value}`;
    
    // Update the specific style property
    const fullPath = `${stylePath}.${property}`;
    console.log(`📏 Updating spacing: ${fullPath} → ${className}`);
    onUpdate(fullPath, className);
  };

  const inferStylePath = (dataPath) => {
    if (!dataPath) return null;
    const parts = dataPath.split('.');
    const lastPart = parts[parts.length - 1].replace(/\[\d+\]/, '');
    return `styles.${lastPart}`;
  };

  // Spacing scale options (Tailwind)
  const spacingOptions = [
    { value: '0', label: '0 (0px)' },
    { value: '0.5', label: '0.5 (2px)' },
    { value: '1', label: '1 (4px)' },
    { value: '1.5', label: '1.5 (6px)' },
    { value: '2', label: '2 (8px)' },
    { value: '2.5', label: '2.5 (10px)' },
    { value: '3', label: '3 (12px)' },
    { value: '4', label: '4 (16px)' },
    { value: '5', label: '5 (20px)' },
    { value: '6', label: '6 (24px)' },
    { value: '8', label: '8 (32px)' },
    { value: '10', label: '10 (40px)' },
    { value: '12', label: '12 (48px)' },
    { value: '16', label: '16 (64px)' },
    { value: '20', label: '20 (80px)' },
    { value: '24', label: '24 (96px)' },
  ];

  const currentValues = activeTab === 'margin' ? margin : padding;
  const handleChange = activeTab === 'margin' ? handleMarginChange : handlePaddingChange;

  return (
    <div className="space-y-4">
      {/* Tab Selector */}
      <div className="flex gap-2">
        <button
          onClick={() => setActiveTab('margin')}
          className={`flex-1 px-3 py-2 text-sm rounded-lg transition-colors ${
            activeTab === 'margin'
              ? 'bg-blue-500 text-white'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          Margin
        </button>
        <button
          onClick={() => setActiveTab('padding')}
          className={`flex-1 px-3 py-2 text-sm rounded-lg transition-colors ${
            activeTab === 'padding'
              ? 'bg-blue-500 text-white'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          Padding
        </button>
      </div>

      {/* Visual Box Model Display */}
      <div className="relative bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
        <div className="text-xs text-blue-600 mb-2 font-medium">
          {activeTab === 'margin' ? 'Margin' : 'Padding'}
        </div>
        
        <div className="bg-white border-2 border-dashed border-blue-300 rounded p-8">
          {/* Top */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2">
            <input
              type="number"
              value={currentValues.top}
              onChange={(e) => handleChange('top', e.target.value)}
              className="w-16 px-2 py-1 text-xs text-center border border-gray-300 rounded"
              min="0"
            />
          </div>
          
          {/* Right */}
          <div className="absolute top-1/2 right-0 -translate-y-1/2">
            <input
              type="number"
              value={currentValues.right}
              onChange={(e) => handleChange('right', e.target.value)}
              className="w-16 px-2 py-1 text-xs text-center border border-gray-300 rounded"
              min="0"
            />
          </div>
          
          {/* Bottom */}
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2">
            <input
              type="number"
              value={currentValues.bottom}
              onChange={(e) => handleChange('bottom', e.target.value)}
              className="w-16 px-2 py-1 text-xs text-center border border-gray-300 rounded"
              min="0"
            />
          </div>
          
          {/* Left */}
          <div className="absolute top-1/2 left-0 -translate-y-1/2">
            <input
              type="number"
              value={currentValues.left}
              onChange={(e) => handleChange('left', e.target.value)}
              className="w-16 px-2 py-1 text-xs text-center border border-gray-300 rounded"
              min="0"
            />
          </div>
          
          {/* Center element representation */}
          <div className="text-center text-xs text-gray-400 py-4">
            Element
          </div>
        </div>
      </div>

      {/* Individual Side Controls */}
      <div className="space-y-3">
        {['top', 'right', 'bottom', 'left'].map(side => (
          <div key={side}>
            <label className="block text-xs font-medium text-gray-700 mb-1 capitalize">
              {side}
            </label>
            <select
              value={currentValues[side]}
              onChange={(e) => handleChange(side, e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {spacingOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        ))}
      </div>

      {/* Quick Presets */}
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-2">
          Quick Apply
        </label>
        <div className="flex gap-2">
          <button
            onClick={() => {
              ['top', 'right', 'bottom', 'left'].forEach(side => handleChange(side, '0'));
            }}
            className="flex-1 px-3 py-2 text-xs bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            None
          </button>
          <button
            onClick={() => {
              ['top', 'right', 'bottom', 'left'].forEach(side => handleChange(side, '4'));
            }}
            className="flex-1 px-3 py-2 text-xs bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Small
          </button>
          <button
            onClick={() => {
              ['top', 'right', 'bottom', 'left'].forEach(side => handleChange(side, '8'));
            }}
            className="flex-1 px-3 py-2 text-xs bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Large
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Parse Tailwind spacing classes to numeric values
 */
function parseSpacingClasses(classes) {
  const values = { top: 0, right: 0, bottom: 0, left: 0 };
  
  if (!classes || classes.length === 0) return values;
  
  classes.forEach(cls => {
    const match = cls.match(/^-?([mp])([trblxy])?-(.+)$/);
    if (!match) return;
    
    const [, , side, value] = match;
    const numValue = value.replace('.', '_');
    
    if (!side) {
      // All sides
      values.top = values.right = values.bottom = values.left = numValue;
    } else if (side === 'x') {
      values.left = values.right = numValue;
    } else if (side === 'y') {
      values.top = values.bottom = numValue;
    } else {
      const sideMap = { t: 'top', r: 'right', b: 'bottom', l: 'left' };
      values[sideMap[side]] = numValue;
    }
  });
  
  return values;
}

